/*=============================================== GettingStarted ===============================================*/

import { useContext, Fragment } from "react"
import { CodeContainer, Section, Text } from "tsx-library-julseb"

import { LangContext } from "context"
import type { LangContextType } from "context"

import { Page } from "components"

import { siteData } from "data"

const GettingStarted = () => {
    const { language } = useContext(LangContext) as LangContextType

    return (
        <Page title="Getting started">
            <Text tag="h1">Getting started</Text>

            <Fragment>
                <Section gap="xs">
                    <Text tag="h2">Install in your project</Text>

                    <Text>Open your app in a terminal and run:</Text>
                </Section>

                <CodeContainer language="shell">
                    {siteData.npmLib}
                </CodeContainer>

                <Text>
                    Then, open your <code>index.js</code> and add this line:
                </Text>

                <CodeContainer language="javascript">
                    {siteData.linkCss}
                </CodeContainer>

                <Text>
                    For every component we are using{" "}
                    <a
                        href="https://styled-components.com/"
                        target="_blank"
                        rel="noreferrer noopener"
                    >
                        Styled Components
                    </a>
                    .
                </Text>
            </Fragment>

            <Fragment>
                <Section gap="xs">
                    <Text tag="h2">Overrides</Text>

                    <Text>
                        To override all variables, create a <code>.css</code>{" "}
                        file, and import it in your <code>index.js</code> file
                        after the library CSS file:
                    </Text>
                </Section>

                <CodeContainer language="javascript">
                    {`${siteData.linkCss}\nimport "./styles/name-of-file.css"`}
                </CodeContainer>

                <Text>
                    Then, in your new <code>css</code> file, create a{" "}
                    <code>:root</code>
                    element and change the variables:
                </Text>

                <CodeContainer language="css">
                    {":root {\n    --color-light-primary-500: #445BE4;\n}"}
                </CodeContainer>
            </Fragment>

            <Fragment>
                <Section gap="xs">
                    <Text tag="h2">Components development</Text>

                    <Text>
                        Because of the way Styled Components is built, we need
                        to use for the development of components{" "}
                        <code>data-attributes</code>, <code>classNames</code>{" "}
                        and <code>CSS variables</code>. If we use Styled
                        Components <code>props</code>, it generates a new class
                        with the same code but the one changed by the prop. For
                        example:
                    </Text>
                </Section>

                {language === "ts" ? (
                    <CodeContainer language="typescript">{`const Button = styled.button<{\n    $color: "red" | "blue"\n    $size?: "default" | "small" | "large"\n}>\`\n    border: none;\n    border-radius: 8px;\n    background-color: \${({ $color }) => $color};\n    font-size: \${({ $size }) =>\n        $size === "large" ? 18 : $size === "small" ? 14 : 16}px;\n    padding: \${({ $size }) =>\n        $size === "large"\n            ? \`6px 12px\`\n            : $size === "small"\n            ? \`2px 4px\`\n            : \`4px 8px\`};\n    color: white;\n\`\n\n<Button $color="red" $size="small">\n    Red\n</Button>\n\n<Button $color="blue" $size="large">\n    Blue\n</Button>`}</CodeContainer>
                ) : (
                    <CodeContainer language="javascript">{`const Button = styled.button\`\n    border: none;\n    border-radius: 8px;\n    background-color: \${({ $color }) => $color};\n    font-size: \${({ $size }) =>\n        $size === "large" ? 18 : $size === "small" ? 14 : 16}px;\n    padding: \${({ $size }) =>\n        $size === "large"\n            ? \`6px 12px\`\n            : $size === "small"\n            ? \`2px 4px\`\n            : \`4px 8px\`};\n    color: white;\n\`\n\n<Button $color="red" $size="small">\n    Red\n</Button>\n\n<Button $color="blue" $size="large">\n    Blue\n</Button>`}</CodeContainer>
                )}

                <Text>would generate:</Text>

                <CodeContainer language="css">{`.jaVbVv {\n    border: none;\n    border-radius: 8px;\n    background-color: red;\n    font-size: 14px;\n    padding: 2px 4px;\n    color: white;\n}\n\n.imDUjV {\n    border: none;\n    border-radius: 8px;\n    background-color: blue;\n    font-size: 18px;\n    padding: 6px 12px;\n    color: white;\n}`}</CodeContainer>

                <Text>
                    To handle this issue, we need to build components like this:
                </Text>

                {language === "ts" ? (
                    <CodeContainer language="typescript">{`const Button = styled.button\`\n    border: none;\n    border-radius: 8px;\n    background-color: var(--button-color);\n    color: white;\n\n    &[data-size="default"] {\n        font-size: 16px;\n        padding: 4px 8px;\n    }\n\n    &[data-size="small"] {\n        font-size: 14px;\n        padding: 2px 4px;\n    }\n\n    &[data-size="large"] {\n        font-size: 18px;\n        padding: 6px 12px;\n    }\n\`\n\n<Button data-size="default" style={{ ["--button-color" as any]: "red"}}>\n    Red\n</Button>\n\n<Button data-size="large" style={{ ["--button-color" as any]: "blue"}}>\n    Blue\n</Button>`}</CodeContainer>
                ) : (
                    <CodeContainer language="javascript">{`const Button = styled.button\`\n    border: none;\n    border-radius: 8px;\n    background-color: var(--button-color);\n    color: white;\n\n    &[data-size="default"] {\n        font-size: 16px;\n        padding: 4px 8px;\n    }\n\n    &[data-size="small"] {\n        font-size: 14px;\n        padding: 2px 4px;\n    }\n\n    &[data-size="large"] {\n        font-size: 18px;\n        padding: 6px 12px;\n    }\n\`\n\n<Button data-size="default" style={{ "--button-color": "red"}}>\n    Red\n</Button>\n\n<Button data-size="large" style={{ "--button-color": "blue"}}>\n    Blue\n</Button>`}</CodeContainer>
                )}

                <Text>
                    And use the <code>className</code> for conditional classes:
                </Text>

                <CodeContainer language="javascript">{`const StyledModal = styled.div\`\n    width: 100vw;\n    height: 100vh;\n    z-index: 999;\n    position: fixed;\n    top: 0;\n    left: 0;\n    background: \${Overlays.Plain.Black80};\n    display: none;\n\n    &.open {\n        display: block;\n    }\n\`\n\nconst Modal = () => {\n    const [isOpen, setIsOpen] = useState(false)\n\n    return (\n        <>\n            <button onClick={() => setIsOpen(true)}>Open modal</button>\n\n            <StyledModal className={classNames({ open: isOpen })}>\n                Modal content...\n            </StyledModal>\n        </>\n    )\n}`}</CodeContainer>

                <Text>
                    Here, we are using{" "}
                    <a
                        href="https://www.npmjs.com/package/classnames"
                        target="_blank"
                        rel="noreferrer noopener"
                    >
                        this package
                    </a>{" "}
                    to handle conditionnal classNames.
                </Text>
            </Fragment>

            <Fragment>
                <Section gap="xs">
                    <Text tag="h2">TypeScript</Text>

                    <Text>
                        For TypeScript users, you can import types in needed
                        components with this file:
                    </Text>
                </Section>

                <CodeContainer language="javascript">
                    {siteData.linkTypes("Type")}
                </CodeContainer>
            </Fragment>
        </Page>
    )
}

export default GettingStarted
